import { ArrowRight, BadgeCheck, Building2, ShieldCheck, UtensilsCrossed } from "lucide-react";
import { Link } from "react-router-dom";
import { industries, principles, processSteps, services, siteMeta } from "@/content/siteContent";

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <section className="bg-slate-950 text-white" data-testid="home-hero-section">
        <div className="mx-auto grid min-h-[78vh] w-full max-w-7xl lg:grid-cols-[0.95fr_1.05fr]">
          <div className="flex items-center px-6 py-16 md:px-12 md:py-24">
            <div className="w-full max-w-3xl space-y-8">
              <div className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.24em] text-orange-400 reveal-up" data-testid="home-hero-kicker">
                <span className="inline-flex h-2 w-2 rounded-full bg-orange-500" />
                Food Service Design & Business Consultants
              </div>
              <h1 className="max-w-3xl text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl reveal-up stagger-1" data-testid="home-hero-heading">
                Strategic food service consulting with a cleaner path from concept to execution.
              </h1>
              <p className="max-w-2xl text-base leading-relaxed text-slate-200 md:text-lg reveal-up stagger-2" data-testid="home-hero-description">
                Concept Magik supports restaurants, hotels, catering operations, and hospitality projects with business and design guidance grounded in efficiency, safety, and long-term operational clarity.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row reveal-up stagger-3">
                <Link
                  className="inline-flex w-full items-center justify-center gap-2 rounded-sm bg-orange-600 px-6 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-orange-500 sm:w-auto"
                  data-testid="home-hero-primary-cta"
                  to="/contact"
                >
                  Contact us
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  className="inline-flex w-full items-center justify-center rounded-sm border border-white/20 px-6 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-white/10 sm:w-auto"
                  data-testid="home-hero-secondary-cta"
                  to="/services"
                >
                  View services
                </Link>
              </div>
              <div className="grid max-w-2xl gap-3 border-t border-white/10 pt-6 sm:grid-cols-3 reveal-up stagger-4" data-testid="home-hero-trust-row">
                {[
                  "Concept development",
                  "Commercial kitchen planning",
                  "Business advisory support",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-sm border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-100"
                    data-testid={`home-hero-trust-item-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="relative min-h-[320px] overflow-hidden border-t border-white/10 lg:min-h-full lg:border-l lg:border-t-0">
            <img
              alt="Chefs working in a professional commercial kitchen"
              className="absolute inset-0 h-full w-full object-cover object-center"
              data-testid="home-hero-image"
              src={siteMeta.heroImage}
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,23,42,0.12),rgba(15,23,42,0.48))] lg:bg-[linear-gradient(90deg,rgba(15,23,42,0.1),rgba(15,23,42,0.42))]" />
          </div>
        </div>
      </section>

      <section className="bg-white py-24 md:py-28" data-testid="home-services-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 md:px-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="space-y-5">
            <div className="section-kicker" data-testid="home-services-kicker">What Concept Magik delivers</div>
            <h2 className="text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl" data-testid="home-services-heading">
              Professional guidance built around real food service pressure.
            </h2>
            <p className="text-base text-slate-600 md:text-lg" data-testid="home-services-description">
              The work is shaped by business priorities, design thinking, and hospitality experience rather than generic templates.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {services.map((service, index) => {
              const Icon = index === 0 ? Building2 : index === 1 ? UtensilsCrossed : index === 2 ? ShieldCheck : BadgeCheck;
              return (
                <article
                  key={service.slug}
                  className="lift-card border border-slate-200 bg-white p-8"
                  data-testid={`service-card-${service.slug}`}
                >
                  <Icon className="h-8 w-8 text-orange-600" />
                  <h3 className="mt-6 text-2xl font-bold text-slate-950" data-testid={`service-card-title-${service.slug}`}>
                    {service.title}
                  </h3>
                  <p className="mt-4 text-base text-slate-600" data-testid={`service-card-summary-${service.slug}`}>
                    {service.summary}
                  </p>
                  <ul className="mt-6 grid gap-3">
                    {service.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="flex items-start gap-3 text-sm text-slate-700"
                        data-testid={`service-card-bullet-${service.slug}-${bullet.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-orange-600" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24 md:py-28" data-testid="home-about-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 md:px-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div className="space-y-6">
            <div className="section-kicker" data-testid="home-about-kicker">Built on mission and commitment</div>
            <h2 className="text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl" data-testid="home-about-heading">
              A client-first consultancy with strict standards.
            </h2>
            <p className="text-base text-slate-600 md:text-lg" data-testid="home-about-mission">
              {siteMeta.mission}
            </p>
            <p className="text-base text-slate-600 md:text-lg" data-testid="home-about-relationship">
              {siteMeta.relationship}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {principles.map((principle) => (
                <article
                  key={principle.slug}
                  className="lift-card border border-slate-200 bg-white p-6"
                  data-testid={`principle-card-${principle.slug}`}
                >
                  <h3 className="text-xl font-bold text-slate-950" data-testid={`principle-card-title-${principle.slug}`}>
                    {principle.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600" data-testid={`principle-card-description-${principle.slug}`}>
                    {principle.description}
                  </p>
                </article>
              ))}
            </div>
          </div>

          <div className="relative overflow-hidden border border-slate-200 bg-white p-4" data-testid="home-about-image-panel">
            <img
              alt="Professional inspection in a hospitality environment"
              className="aspect-[4/5] w-full object-cover object-center"
              data-testid="home-about-image"
              src={siteMeta.operationsImage}
            />
          </div>
        </div>
      </section>

      <section className="bg-white py-24 md:py-28" data-testid="home-industries-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl space-y-5">
              <div className="section-kicker" data-testid="home-industries-kicker">Who this serves</div>
              <h2 className="text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl" data-testid="home-industries-heading">
                Designed for the full spectrum of food service operators.
              </h2>
            </div>
            <Link
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-900 transition-colors hover:bg-slate-100"
              data-testid="home-industries-link"
              to="/industries"
            >
              View industries
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {industries.map((industry) => (
              <article
                key={industry.slug}
                className="lift-card border border-slate-200 bg-slate-50 p-8"
                data-testid={`industry-card-${industry.slug}`}
              >
                <h3 className="text-2xl font-bold text-slate-950" data-testid={`industry-card-title-${industry.slug}`}>
                  {industry.title}
                </h3>
                <p className="mt-4 text-base text-slate-600" data-testid={`industry-card-description-${industry.slug}`}>
                  {industry.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-24 text-white md:py-28" data-testid="home-process-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-3xl space-y-5">
            <div className="section-kicker text-orange-400" data-testid="home-process-kicker">How the work is approached</div>
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl" data-testid="home-process-heading">
              Structure first. Creativity where it matters. Client interests always protected.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {processSteps.map((step, index) => (
              <article
                key={step.slug}
                className="lift-card border border-white/10 bg-white/5 p-8"
                data-testid={`process-step-card-${step.slug}`}
              >
                <div className="text-sm font-bold uppercase tracking-[0.24em] text-orange-400" data-testid={`process-step-number-${step.slug}`}>
                  0{index + 1}
                </div>
                <h3 className="mt-5 text-2xl font-bold text-white" data-testid={`process-step-title-${step.slug}`}>
                  {step.title}
                </h3>
                <p className="mt-4 text-base text-slate-300" data-testid={`process-step-description-${step.slug}`}>
                  {step.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
